import { useCallback } from "react"

import type { PermissionActionType } from "@/constants/permissions"
import { useCurrentUserStore } from "@/stores/currentUserStore"

/** Single permission (OR with others) or AND-group of permissions */
export type PermissionRequirement = PermissionActionType | readonly PermissionActionType[]

/**
 * Checks user permissions against a list of requirements.
 * Top-level array is OR: at least one requirement must pass.
 * - Single permission: user must have it.
 * - Array of permissions: user must have ALL of them (AND).
 * @example [p1, p2] → p1 || p2
 * @example [p1, [p2, p3]] → p1 || (p2 && p3)
 */
export type PermissionRequirements = readonly PermissionRequirement[]

interface UsePermissionManagerReturn {
  /** Returns true if the current user satisfies at least one of the given permission requirements (OR). */
  hasPermissions: (requirements: PermissionRequirements) => boolean
}

function checkRequirement(requirement: PermissionRequirement, userPermissions: PermissionActionType[]): boolean {
  if (Array.isArray(requirement)) {
    return requirement.length > 0 && requirement.every((p) => userPermissions.includes(p))
  }
  const singlePermission = requirement as PermissionActionType
  return userPermissions.includes(singlePermission)
}

/**
 * Hook for checking current user permissions against flexible OR/AND requirements.
 *
 * - **Top-level list**: OR — at least one requirement must pass.
 * - **Single permission**: user must have that permission.
 * - **Nested array**: AND — user must have every permission in the array.
 *
 * @returns `{ hasPermissions }` — call with a list of requirements to check.
 *
 * @example Single or multiple (OR)
 * hasPermissions([PermissionActionType.PRODUCT_MAINTENANCE])
 * hasPermissions([PermissionActionType.X, PermissionActionType.Y])  // X || Y
 *
 * @example With AND-group
 * hasPermissions([PermissionActionType.X, [PermissionActionType.Y, PermissionActionType.Z]])  // X || (Y && Z)
 */
export function usePermissionManager(): UsePermissionManagerReturn {
  const currentUser = useCurrentUserStore(({ currentUser }) => currentUser)

  const hasPermissions = useCallback(
    (requirements: PermissionRequirements) => {
      if (!currentUser?.permissions?.length) return false

      const userPermissions = currentUser.permissions as PermissionActionType[]
      return requirements.some((req) => checkRequirement(req, userPermissions))
    },
    [currentUser?.permissions]
  )

  return {
    hasPermissions,
  }
}
